/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button } from 'react-bootstrap';

type Props = {
  children: ReactNode;
  resetKey?: string | number;
};

type State = {
  error: Error | null;
};

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading as="h5">This page could not be displayed</Alert.Heading>
        <p className="mb-2">An unexpected error occurred while showing this content.</p>
        <Button size="sm" variant="outline-danger" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </Alert>
    );
  }
}
